import { useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGridIcon, ListIcon } from "lucide-react";

import { useCameras } from "@/api/cameras";
import { useStatusFeed } from "@/api/status_feed";
import { CameraFeedPlayer } from "@/components/CameraFeedPlayer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { statusLabel, connectionLabel } from "@/lib/vocab";

type GridSize = "2x2" | "3x3";

const gridClass: Record<GridSize, string> = {
  "2x2": "grid grid-cols-1 gap-3 md:grid-cols-2",
  "3x3": "grid grid-cols-1 gap-3 md:grid-cols-3",
};

const tileLimit: Record<GridSize, number> = {
  "2x2": 4,
  "3x3": 9,
};

export function CameraGridPage() {
  const [size, setSize] = useState<GridSize>("2x2");
  const { data, isLoading, isError } = useCameras({
    limit: tileLimit[size],
  });
  const { statuses, connected } = useStatusFeed();

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Live grid</h1>
          <p className="text-sm text-muted-foreground">
            {connected ? (
              <span className="text-emerald-600">● live</span>
            ) : (
              <span className="text-muted-foreground">○ connecting</span>
            )}
            {data && (
              <>
                {" · "}
                {Math.min(items.length, tileLimit[size])} of {data.total} cameras
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={size === "2x2" ? "default" : "outline"}
            size="sm"
            onClick={() => setSize("2x2")}
          >
            <LayoutGridIcon className="mr-1 h-4 w-4" />
            2 × 2
          </Button>
          <Button
            variant={size === "3x3" ? "default" : "outline"}
            size="sm"
            onClick={() => setSize("3x3")}
          >
            <LayoutGridIcon className="mr-1 h-4 w-4" />
            3 × 3
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/cameras">
              <ListIcon className="mr-1 h-4 w-4" />
              List
            </Link>
          </Button>
        </div>
      </div>

      {isLoading && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Loading cameras…
          </CardContent>
        </Card>
      )}
      {isError && (
        <Card className="border-destructive/40">
          <CardContent className="py-10 text-center text-sm text-destructive">
            Could not load cameras.
          </CardContent>
        </Card>
      )}

      {data && items.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No cameras to display.
          </CardContent>
        </Card>
      )}

      {data && items.length > 0 && (
        <div className={cn(gridClass[size])}>
          {items.slice(0, tileLimit[size]).map((c) => {
            const liveStatus = statuses[c.id] ?? c.status;
            const isOnline = liveStatus === "online";
            return (
              <Card key={c.id} className="overflow-hidden">
                <Link to={`/cameras/${c.id}`} className="block">
                  <CameraFeedPlayer
                    hlsUrl={c.hls_url ?? undefined}
                    cameraId={c.id}
                    online={isOnline}
                    cameraName={c.name}
                    autoplay
                    controls={false}
                  />
                </Link>
                <CardContent className="flex items-center justify-between p-3 text-sm">
                  <div className="truncate">
                    <p className="truncate font-medium">{c.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {c.manufacturer ?? "—"}
                      {c.model ? ` · ${c.model}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge
                      variant={
                        liveStatus === "online"
                          ? "online"
                          : liveStatus === "error"
                            ? "error"
                            : "offline"
                      }
                    >
                      {statusLabel(liveStatus)}
                    </Badge>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {connectionLabel(c.connection_mode)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
